import type { RichTextDocument } from "@/components/editor/types";
import { normalizeNoteContent } from "@/components/notes/noteContent";

const ANONYMOUS_NOTEBOOK_STORAGE_KEY = "west-marches-map.anonymous-notebook";

export const DEFAULT_ANONYMOUS_NOTEBOOK_CONTENT: RichTextDocument = {
  type: "doc",
  content: [
    {
      type: "heading",
      attrs: { level: 1 },
      content: [{ type: "text", text: "Welcome to West Marches" }],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "You can explore the example world right away, or start fresh to open a blank sea of water tiles and build from scratch.",
        },
      ],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "This notebook is view-only while you are signed out. When you create your first account, this welcome note will be copied into your Game Master notebook so you can keep editing it there.",
        },
      ],
    },
  ],
};

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && "localStorage" in window;
}

export function loadAnonymousNotebookContent(): RichTextDocument {
  if (!canUseLocalStorage()) {
    return DEFAULT_ANONYMOUS_NOTEBOOK_CONTENT;
  }

  const raw = window.localStorage.getItem(ANONYMOUS_NOTEBOOK_STORAGE_KEY);
  if (!raw) {
    return DEFAULT_ANONYMOUS_NOTEBOOK_CONTENT;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    return normalizeNoteContent(parsed);
  } catch {
    return DEFAULT_ANONYMOUS_NOTEBOOK_CONTENT;
  }
}

export function saveAnonymousNotebookContent(content: RichTextDocument): void {
  if (!canUseLocalStorage()) {
    return;
  }

  window.localStorage.setItem(
    ANONYMOUS_NOTEBOOK_STORAGE_KEY,
    JSON.stringify(normalizeNoteContent(content)),
  );
}

export function ensureAnonymousNotebookContent(): RichTextDocument {
  const content = loadAnonymousNotebookContent();
  if (canUseLocalStorage()) {
    saveAnonymousNotebookContent(content);
  }
  return content;
}

export function clearAnonymousNotebookContent(): void {
  if (!canUseLocalStorage()) {
    return;
  }

  window.localStorage.removeItem(ANONYMOUS_NOTEBOOK_STORAGE_KEY);
}
